import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  useMediaQuery,
  Paper,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardMedia,
  Button,
  CircularProgress,
  Avatar,
  Stack,
  Divider,
  useTheme,
  IconButton,
  TextField,
  Grid,
  Badge,
  Chip,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from "@mui/material";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import { fetchAllNftsOwnedByUser } from "../../../redux/nftSlice";
import CowIcon from "../../CowIcon";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import { NftAssetDto } from "../../../models/NftAssetDto";
import { Loan, LoanPool, LoanStatus } from "../../../types/loanTypes";
import { RootState } from "../../../redux/store";
import {
  setCurrentLoanPoolView,
  setSelectedAssetTab,
  setSelectedLoanPool,
} from "../../../redux/uiSlice";
import PoolListItem from "../../Loans/PoolListItem";
import InvestmentListView from "../../Loans/InvestmentListView";
import InvestmentView from "../../Loans/InvestmentView";
import AssetsView from "../../Assets/AssetsView";

// Get width from environment variable with fallback
const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

type LoanView = "list" | "new-loan" | "repayment";

const AssetManagementView: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const dispatch = useAppDispatch();

  // State from Redux
  const { selectedAssetTab } = useAppSelector((state: RootState) => state.ui);
  const { allMyOpenSeaNfts } = useAppSelector((state) => state.nft);

  // Local state
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentLoanView, setCurrentLoanView] = useState<LoanView>("list");
  const [selectedLoan, setSelectedLoan] = useState<Loan | null>(null);
  const [activeTab, setActiveTab] = useState<AssetTab>(
    selectedAssetTab || "nfts"
  );

  // Sample data - in a real app, this would come from API/blockchain
  const personalLoans: Loan[] = [
    {
      id: "1",
      cattlePhoto: "ipfs://...",
      amount: 500,
      currency: "NDEIPI",
      interestRate: 12,
      term: 6,
      status: "active" as LoanStatus,
      nextPayment: "2023-11-15",
      cattleDetails: {
        tagNumber: "BOV-001",
        breed: "Angus",
        age: 3,
        estimatedValue: 800,
      },
    },
    {
      id: "2",
      cattlePhoto: "ipfs://...",
      amount: 400,
      currency: "NDEIPI",
      interestRate: 12,
      term: 6,
      status: "active" as LoanStatus,
      nextPayment: "2023-11-15",
      cattleDetails: {
        tagNumber: "BOV-003",
        breed: "Nkone",
        age: 2,
        estimatedValue: 900,
      },
    },
    {
      id: "3",
      cattlePhoto: "ipfs://...",
      amount: 800,
      currency: "NDEIPI",
      interestRate: 12,
      term: 6,
      status: "active" as LoanStatus,
      nextPayment: "2023-12-15",
      cattleDetails: {
        tagNumber: "BOV-001",
        breed: "Angus",
        age: 3,
        estimatedValue: 900,
      },
    },
  ];

  const investmentPools: LoanPool[] = [
    {
      id: "pool-1",
      name: "Premium Cattle Pool",
      totalValue: 25000,
      activeLoans: 42,
      defaultRate: 2.5,
      avgInterest: 11.2,
    },
  ];

  // Filter NFTs based on search query
  const filteredNFTs = allMyOpenSeaNfts?.nfts.filter((nft) =>
    nft.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Load user's NFTs
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        await dispatch(fetchAllNftsOwnedByUser());
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dispatch]);

  // Handle tab change
  const handleTabChange = (event: React.SyntheticEvent, newValue: AssetTab) => {
    setActiveTab(newValue);
    dispatch(setSelectedAssetTab(newValue));
    // Reset loan view when switching tabs
    setCurrentLoanView("list");
  };

  // Handle loan view changes
  const handleNewLoan = () => setCurrentLoanView("new-loan");
  const handleRepayment = (loan: Loan) => {
    setSelectedLoan(loan);
    setCurrentLoanView("repayment");
  };
  const handleBackToList = () => setCurrentLoanView("list");

  // Scrollable content styles
  const scrollableBoxStyles = {
    maxHeight: isMobile ? "76vh" : "68vh",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": {
      display: "none",
    },
    p: isMobile ? 1 : 2,
  };

  return (
    <Paper
      sx={{
        p: isMobile ? 1 : 2,
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        height: isMobile ? "" : "850px",
        ...(isMobile && {
          maxWidth: "100%",
          borderRadius: 0,
          boxShadow: "none",
        }),
        ...(!isMobile && {
          borderRadius: 2,
          boxShadow: 3,
        }),
      }}
    >
      {/* Header Section */}
      <Box sx={{ p: isMobile ? 1 : 2, textAlign: "start" }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 1,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              fontSize: isMobile ? "1.5rem" : "2rem",
              fontWeight: 600,
            }}
          >
            {activeTab === "nfts"
              ? "NFT Collection"
              : activeTab === "loans"
              ? "My Cattle Loans"
              : activeTab === "assets"
              ? "Real World Assets"
              : "Investment Pools"}
          </Typography>
          <IconButton
            color="inherit"
            aria-label="cart"
            size={isMobile ? "small" : "medium"}
          >
            <Badge badgeContent={0} color="error">
              <ShoppingCartIcon fontSize={isMobile ? "medium" : "large"} />
            </Badge>
          </IconButton>
        </Box>
        <Typography
          variant="body1"
          gutterBottom
          sx={{ fontSize: isMobile ? "0.8rem" : "1rem" }}
        >
          {activeTab === "nfts"
            ? "Browse your NFT collection"
            : activeTab === "loans"
            ? "Manage your cattle-backed loans"
            : activeTab === "assets"
            ? "Invest in Real World Assets"
            : "Invest in cattle loan pools"}
        </Typography>
      </Box>
      <Divider />

      {/* Main Tabs */}
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        variant="fullWidth"
        sx={{ mb: 2 }}
      >
        <Tab label="NFTs" value="nfts" />
        <Tab label="Assets" value="assets" />
        <Tab label="Loans" value="loans" />
        <Tab label="Invest" value="invest" />
      </Tabs>

      {/* Content Area */}
      {activeTab === "nfts" && (
        <NFTListView
          nfts={filteredNFTs || []}
          searchQuery={searchQuery}
          onSearchChange={(e) => setSearchQuery(e.target.value)}
          isMobile={isMobile}
          scrollableBoxStyles={scrollableBoxStyles}
        />
      )}

      {activeTab === "loans" && currentLoanView === "list" && (
        <PersonalLoansListView
          loans={personalLoans}
          onNewLoan={handleNewLoan}
          onRepayment={handleRepayment}
          isMobile={isMobile}
        />
      )}

      {activeTab === "loans" && currentLoanView === "new-loan" && (
        <NewLoanView isMobile={isMobile} onBack={handleBackToList} />
      )}

      {activeTab === "loans" && currentLoanView === "repayment" && (
        <RepaymentView
          loan={selectedLoan!}
          isMobile={isMobile}
          onBack={handleBackToList}
        />
      )}

      {activeTab === "invest" && (
        <InvestmentView pools={investmentPools} isMobile={isMobile} />
      )}
      {activeTab === "assets" && <AssetsView />}
    </Paper>
  );
};

// Sub-components for each view

const NFTListView: React.FC<{
  nfts: NftAssetDto[];
  searchQuery: string;
  onSearchChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  isMobile: boolean;
  scrollableBoxStyles: any;
}> = ({ nfts, searchQuery, onSearchChange, isMobile, scrollableBoxStyles }) => {
  return (
    <>
      <Box sx={{ p: isMobile ? 1 : 2 }}>
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Search NFTs or collections..."
          value={searchQuery}
          onChange={onSearchChange}
          sx={{ mb: isMobile ? 1 : 2 }}
          size={isMobile ? "small" : "medium"}
        />
      </Box>

      <Grid container spacing={isMobile ? 1 : 2} sx={scrollableBoxStyles}>
        {nfts.map((nft) => (
          <Grid key={nft.identifier}>
            <Card sx={{ height: "100%" }}>
              <CardMedia
                component="img"
                image={nft.image_url}
                alt={nft.name}
                sx={{
                  height: isMobile ? 120 : 180,
                  objectFit: "cover",
                }}
              />
              <CardContent>
                <Typography gutterBottom variant="h6" component="div">
                  {nft.name}
                </Typography>
                <Chip
                  label={nft.collection || "Unknown Collection"}
                  size="small"
                />
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );
};

const PersonalLoansListView: React.FC<{
  loans: Loan[];
  onNewLoan: () => void;
  onRepayment: (loan: Loan) => void;
  isMobile: boolean;
}> = ({ loans, onNewLoan, onRepayment, isMobile }) => {
  const theme = useTheme();

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Button
          variant="contained"
          onClick={onNewLoan}
          size={isMobile ? "small" : "medium"}
          startIcon={<CowIcon fill={theme.palette.common.white} />}
        >
          New Loan
        </Button>
      </Box>

      {loans.length === 0 ? (
        <EmptyState
          icon={<CowIcon fill={theme.palette.text.secondary} size={48} />}
          title="No Active Loans"
          description="Use your cattle as collateral to get a loan for farming inputs"
          action={
            <Button variant="contained" onClick={onNewLoan}>
              Apply for Loan
            </Button>
          }
        />
      ) : (
        <List sx={{ width: "100%" }}>
          {loans.map((loan) => (
            <Card key={loan.id} sx={{ mb: 2 }}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: "secondary.main" }}>
                    <CowIcon fill="#fff" />
                  </Avatar>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1">
                      {loan.cattleDetails.breed} #{loan.cattleDetails.tagNumber}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {loan.amount} {loan.currency} • {loan.interestRate}% APR
                    </Typography>
                  </Box>
                  <Chip
                    label={loan.status}
                    size="small"
                    color={
                      loan.status === "active"
                        ? "success"
                        : loan.status === "defaulted"
                        ? "error"
                        : "warning"
                    }
                  />
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack direction="row" justifyContent="space-between">
                  <Box>
                    <Typography variant="caption">Next Payment</Typography>
                    <Typography>{loan.nextPayment}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption">Amount Due</Typography>
                    <Typography>
                      {Math.round(loan.amount / loan.term)} {loan.currency}
                    </Typography>
                  </Box>
                </Stack>

                <Button
                  fullWidth
                  variant="outlined"
                  sx={{ mt: 2 }}
                  onClick={() => onRepayment(loan)}
                >
                  Make Payment
                </Button>
              </CardContent>
            </Card>
          ))}
        </List>
      )}
    </Box>
  );
};

const NewLoanView: React.FC<{ isMobile: boolean; onBack: () => void }> = ({
  isMobile,
  onBack,
}) => {
  const theme = useTheme();
  const [photo, setPhoto] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    tagNumber: "",
    breed: "",
    age: "",
    estimatedValue: "",
    healthStatus: "",
    location: "",
    loanAmount: "",
    loanTerm: "6",
  });

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result) {
          setPhoto(event.target.result as string);
        }
      };
      reader.readAsDataURL(e.target.files[0]);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log({ photo, ...formData });
    onBack(); // Return to list after submission
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1 }}>
          New Cattle Loan
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          {/* Photo Upload */}
          <Box sx={{ textAlign: "center" }}>
            <label htmlFor="cattle-photo">
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  margin: "0 auto",
                  bgcolor: photo ? "transparent" : "grey.200",
                  cursor: "pointer",
                }}
              >
                {photo ? (
                  <img
                    src={photo}
                    alt="Cattle"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <CowIcon size={48} fill={theme.palette.text.secondary} />
                )}
              </Avatar>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                {photo ? "Change photo" : "Upload cattle photo"}
              </Typography>
              <input
                id="cattle-photo"
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handlePhotoUpload}
                style={{ display: "none" }}
              />
            </label>
          </Box>

          {/* Cattle Details */}
          <TextField
            label="Tag Number"
            name="tagNumber"
            value={formData.tagNumber}
            onChange={handleChange}
            fullWidth
            required
          />

          <TextField
            label="Breed"
            name="breed"
            value={formData.breed}
            onChange={handleChange}
            fullWidth
            required
          />

          <TextField
            label="Age (years)"
            name="age"
            type="number"
            value={formData.age}
            onChange={handleChange}
            fullWidth
            required
          />

          <TextField
            label="Estimated Value (NDEIPI)"
            name="estimatedValue"
            type="number"
            value={formData.estimatedValue}
            onChange={handleChange}
            fullWidth
            required
          />

          <TextField
            label="Health Status"
            name="healthStatus"
            value={formData.healthStatus}
            onChange={handleChange}
            fullWidth
          />

          <TextField
            label="Location"
            name="location"
            value={formData.location}
            onChange={handleChange}
            fullWidth
          />

          {/* Loan Details */}
          <Divider sx={{ my: 1 }} />

          <TextField
            label="Loan Amount (NDEIPI)"
            name="loanAmount"
            type="number"
            value={formData.loanAmount}
            onChange={handleChange}
            fullWidth
            required
          />

          <TextField
            label="Loan Term (months)"
            name="loanTerm"
            select
            value={formData.loanTerm}
            onChange={handleChange}
            fullWidth
            SelectProps={{
              native: true,
            }}
          >
            {[3, 6, 9, 12].map((term) => (
              <option key={term} value={term}>
                {term} months
              </option>
            ))}
          </TextField>

          <Box sx={{ mt: 2 }}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
            >
              Submit Loan Request
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
};

const RepaymentView: React.FC<{
  loan: Loan;
  isMobile: boolean;
  onBack: () => void;
}> = ({ loan, isMobile, onBack }) => {
  const [repaymentHistory, setRepaymentHistory] = useState([
    {
      loanId: "1",
      amount: 100,
      date: "2023-10-15",
      status: "completed" as const,
    },
    {
      loanId: "1",
      amount: 100,
      date: "2023-09-15",
      status: "completed" as const,
    },
  ]);

  const handleRepayment = () => {
    console.log("Processing repayment for loan:", loan.id);
    onBack(); // Return to list after repayment
  };

  return (
    <Box sx={{ p: isMobile ? 1 : 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
        <IconButton onClick={onBack}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ ml: 1 }}>
          Loan Repayment
        </Typography>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction="row" alignItems="center" spacing={2}>
            <Avatar sx={{ bgcolor: "secondary.main" }}>
              <CowIcon fill="#fff" />
            </Avatar>
            <Box>
              <Typography variant="subtitle1">
                {loan.cattleDetails.breed} #{loan.cattleDetails.tagNumber}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {loan.amount} {loan.currency} • {loan.interestRate}% APR
              </Typography>
            </Box>
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack spacing={2}>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography>Next Payment:</Typography>
              <Typography fontWeight="medium">{loan.nextPayment}</Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography>Amount Due:</Typography>
              <Typography fontWeight="medium">
                {Math.round(loan.amount / loan.term)} {loan.currency}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Typography>Remaining Balance:</Typography>
              <Typography fontWeight="medium">
                {loan.amount -
                  repaymentHistory.length *
                    Math.round(loan.amount / loan.term)}{" "}
                {loan.currency}
              </Typography>
            </Box>
          </Stack>

          <Button
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 3 }}
            onClick={handleRepayment}
          >
            Make Payment
          </Button>
        </CardContent>
      </Card>

      <Typography variant="subtitle1" sx={{ mb: 2 }}>
        Payment History
      </Typography>

      {repaymentHistory.length === 0 ? (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textAlign: "center", py: 3 }}
        >
          No repayment history yet
        </Typography>
      ) : (
        <List sx={{ width: "100%" }}>
          {repaymentHistory.map((payment, index) => (
            <ListItem key={index} divider>
              <ListItemAvatar>
                <Avatar
                  sx={{
                    bgcolor:
                      payment.status === "completed"
                        ? "success.main"
                        : "warning.main",
                  }}
                >
                  {payment.status === "completed" ? "✓" : "!"}
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={`${payment.amount} ${loan.currency}`}
                secondary={`Paid on ${payment.date}`}
              />
            </ListItem>
          ))}
        </List>
      )}
    </Box>
  );
};

const EmptyState: React.FC<{
  icon: React.ReactNode;
  title: string;
  description: string;
  action?: React.ReactNode;
}> = ({ icon, title, description, action }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        p: 4,
        textAlign: "center",
        gap: 2,
      }}
    >
      {icon}
      <Typography variant="h6">{title}</Typography>
      <Typography variant="body1" color="text.secondary">
        {description}
      </Typography>
      {action && <Box sx={{ mt: 2 }}>{action}</Box>}
    </Box>
  );
};

export default AssetManagementView;
